
import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import masterData from '../../../data/master_data.json';

interface CityOption {
  label: string;
  path: string;
  state?: string;
  country: string;
}

const buildCityOptions = (): CityOption[] => {
  const options: CityOption[] = [];
  const { countries } = masterData;

  // US cities
  const usa = countries.usa;
  if (usa?.states) {
    Object.values(usa.states).forEach((state: any) => {
      if (state.cities) {
        Object.values(state.cities).forEach((city: any) => {
          options.push({
            label: `${city.name}, ${state.name}`,
            path: `/usa/${state.slug}/${city.slug}`,
            state: state.name,
            country: 'USA',
          });
        });
      }
    });
  }

  // UK regions
  const uk = countries.uk;
  if (uk?.regions) {
    Object.values(uk.regions).forEach((region: any) => {
      options.push({
        label: `${region.name}, UK`,
        path: `/uk/${region.slug}`,
        country: 'UK',
      });
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [salary, setSalary] = useState('75000');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const allCities = useMemo(() => buildCityOptions(), []);

  const filteredCities = useMemo(() => {
    if (!searchQuery.trim()) return allCities.slice(0, 8);
    const q = searchQuery.toLowerCase();
    return allCities.filter((c) => c.label.toLowerCase().includes(q)).slice(0, 8);
  }, [searchQuery, allCities]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelectCity = (city: CityOption) => {
    setSelectedCity(city);
    setSearchQuery(city.label);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsDropdownOpen(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filteredCities.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredCities.length - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectCity(filteredCities[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleCalculate = () => {
    const numericSalary = Number(salary.replace(/,/g, ''));
    if (isNaN(numericSalary) || numericSalary <= 0) return;
    const targetPath = selectedCity ? selectedCity.path : '/usa/ny/manhattan';
    navigate(targetPath);
  };

  const popularCities = useMemo(
    () =>
      [
        allCities.find((c) => c.path === '/usa/ny/manhattan'),
        allCities.find((c) => c.path === '/usa/ca/san-francisco'),
        allCities.find((c) => c.path === '/usa/tx/austin'),
        allCities.find((c) => c.path === '/usa/fl/miami'),
      ].filter(Boolean) as CityOption[],
    [allCities]
  );

  return (
    <section className="relative min-h-[680px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/af8b643bdeaed6e124029cc6fed8e046.jpg"
          alt="Salary tax calculator hero background - calculate your take-home pay"
          title="Free salary tax calculator 2026"
          className="w-full h-full object-cover object-top"
          width="1920"
          height="900"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/20 border border-teal-500/30 rounded-full text-teal-300 text-sm font-medium mb-6">
              <i className="ri-calculator-line"></i> Updated for 2026 Tax Year
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              <strong>Calculate Your Exact Take-Home Pay</strong>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-xl leading-relaxed">
              Free <strong>salary tax calculator</strong> with accurate federal, state, and local tax
              breakdowns for <strong>200+ US cities</strong> and the UK. Know exactly what you keep from
              every paycheck.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <a
                href="/usa/ny/manhattan"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/usa/ny/manhattan');
                }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap text-base shadow-lg shadow-teal-500/25"
              >
                <i className="ri-calculator-line"></i> Calculate Now
              </a>
              <a
                href="/states"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/states');
                }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-map-pin-line"></i> Browse All States
              </a>
            </div>

            {/* Trust Stats */}
            <div className="flex flex-wrap gap-8">
              <div>
                <span className="text-3xl font-bold text-white">2.4M+</span>
                <p className="text-sm text-gray-400 mt-0.5">Calculations Done</p>
              </div>
              <div className="w-px h-12 bg-white/20"></div>
              <div>
                <span className="text-3xl font-bold text-white">200+</span>
                <p className="text-sm text-gray-400 mt-0.5">Cities Covered</p>
              </div>
              <div className="w-px h-12 bg-white/20"></div>
              <div>
                <span className="text-3xl font-bold text-teal-400">4.8</span>
                <p className="text-sm text-gray-400 mt-0.5">User Rating</p>
              </div>
            </div>
          </div>

          {/* Right - Quick Calculator Card */}
          <div className="hidden lg:block">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 shadow-2xl">
              <h2 className="text-xl font-bold text-white mb-6">
                <strong>Quick Salary Estimate</strong>
              </h2>
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-300 mb-2">Annual Salary</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400">$</span>
                  <input
                    type="text"
                    value={Number(salary.replace(/,/g, '')).toLocaleString()}
                    onChange={(e) => setSalary(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-10 pr-4 py-4 text-2xl font-semibold bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                    placeholder="75,000"
                  />
                </div>
              </div>

              {/* Location Search */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Search Location</label>
                <div className="relative" ref={dropdownRef}>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-search-line text-base"></i>
                    </span>
                    <input
                      ref={inputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedCity(null);
                        setIsDropdownOpen(true);
                        setHighlightedIndex(0);
                      }}
                      onFocus={() => setIsDropdownOpen(true)}
                      onKeyDown={handleKeyDown}
                      className="w-full pl-11 pr-10 py-3.5 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 transition-colors"
                      placeholder="Type a city or state..."
                    />
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCity(null);
                          setIsDropdownOpen(false);
                          inputRef.current?.focus();
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-base"></i>
                      </button>
                    )}
                  </div>

                  {isDropdownOpen && (
                    <ul
                      ref={listRef}
                      className="absolute z-50 w-full mt-2 max-h-56 overflow-y-auto bg-gray-900/95 backdrop-blur-lg border border-white/15 rounded-xl shadow-2xl py-1.5"
                    >
                      {filteredCities.length > 0 ? (
                        filteredCities.map((city, idx) => (
                          <li
                            key={city.path}
                            onClick={() => handleSelectCity(city)}
                            onMouseEnter={() => setHighlightedIndex(idx)}
                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors text-sm ${
                              highlightedIndex === idx
                                ? 'bg-teal-500/20 text-teal-300'
                                : 'text-gray-300 hover:bg-white/5'
                            } ${selectedCity?.path === city.path ? 'text-teal-400' : ''}`}
                          >
                            <span className="w-5 h-5 flex items-center justify-center shrink-0">
                              <i className="ri-map-pin-2-fill text-sm"></i>
                            </span>
                            <span className="truncate">{city.label}</span>
                            <span className="ml-auto text-xs text-gray-500 shrink-0">{city.country}</span>
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-3 text-sm text-gray-500 text-center">No locations found</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>

              {/* Popular Quick Picks */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-2">Popular</p>
                <div className="flex flex-wrap gap-2">
                  {popularCities.map((loc) => (
                    <button
                      key={loc.path}
                      onClick={() => handleSelectCity(loc)}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap border ${
                        selectedCity?.path === loc.path
                          ? 'bg-teal-500/25 border-teal-500/50 text-teal-300'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:bg-teal-500/10 hover:border-teal-500/30 hover:text-teal-300'
                      }`}
                    >
                      {loc.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleCalculate}
                className="w-full py-4 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
              >
                Calculate Take-Home Pay
              </button>

              {selectedCity && (
                <p className="mt-3 text-xs text-gray-400 text-center flex items-center justify-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-teal-400 text-xs"></i>
                  </span>
                  Calculating for{' '}
                  <span className="text-teal-300 font-medium">{selectedCity.label}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
